import { DocumentId, type ModelEffort, type StudioModel } from "@curriculum-studio/core";
import { HttpClient, HttpClientRequest, HttpClientResponse } from "@effect/platform";
import { Data, Effect, Schema } from "effect";
import { runtime } from "./runtime.ts";

// The l2_unit_plan launch surface's web client — a document-less sibling of `launch-actions.ts`.
// See `unit-plan-generator-client.ts`'s module doc for why this is a separate surface rather than
// another `environment` on the existing launch endpoints.
//
// U5b/U6 (CON-1316, KTD9/KTD10): `startUnitPlan` is the main thing this surface does — the server
// answers with the pending `documentId` it minted, not a `runId`, and there is no status/list client
// here: the curricula index learns a run's outcome by re-fetching `/api/curricula` (its
// `generationStatus` fold) rather than polling this port directly. `cancelUnitPlan` is the one
// exception (CON-1316 cancel): it is keyed by `documentId` the same way, posting to the REST gate's
// own `/api/generate/cancel`.
//
// Posts to the unified `/api/curricula/create` endpoint's generate branch (`_tag: "generate"`) —
// see that endpoint's doc comment in `rest.ts` for why import and generate now share one wire shape.

// ADR-0107 Decision 4 — one language-pack document a run was given.
export const UnitPlanInputWire = Schema.Struct({
  path: Schema.String,
  kind: Schema.String,
  documentClass: Schema.Literal("language-pack", "reference-guide"),
  // Absent for a reference guide — those documents are not per-language.
  languageCode: Schema.optional(Schema.String),
  // The store revision at the moment of the launch. Compare against the store's current revision to
  // see whether the document has been edited since this run read it.
  revision: Schema.Number,
  // When it was last modified, as at the launch.
  updatedAt: Schema.String,
  sha256: Schema.String,
  volumePath: Schema.String,
});
export type UnitPlanInputWire = typeof UnitPlanInputWire.Type;

// `recorded: false` is not an empty list: a run launched before ADR-0107 shipped has no manifest at
// all, and saying so is more honest than showing nothing.
export const UnitPlanInputsWire = Schema.Struct({
  recorded: Schema.Boolean,
  inputs: Schema.Array(UnitPlanInputWire),
});
export type UnitPlanInputsWire = typeof UnitPlanInputsWire.Type;

export class UnitPlanRequestFailed extends Data.TaggedError("UnitPlanRequestFailed")<{
  readonly status: number;
  readonly message: string;
}> {}

const ErrorBody = Schema.Struct({ message: Schema.optional(Schema.String) });

const failureMessage = (response: HttpClientResponse.HttpClientResponse) =>
  HttpClientResponse.schemaBodyJson(ErrorBody)(response).pipe(
    Effect.map((body) => body.message),
    Effect.orElseSucceed(() => undefined),
  );

const post = (path: string, body: unknown, fallback: string) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const request = yield* HttpClientRequest.post(path).pipe(HttpClientRequest.bodyJson(body));
    const response = yield* client.execute(request);
    if (response.status >= 400) {
      const message = yield* failureMessage(response);
      return yield* Effect.fail(
        new UnitPlanRequestFailed({
          status: response.status,
          message: message ?? `${fallback} (HTTP ${response.status}).`,
        }),
      );
    }
    return response;
  }).pipe(Effect.scoped);

export interface UnitPlanParams {
  readonly cefrLevel: string;
  readonly l1: string;
  readonly l2: string;
  // CON-3203 (ADR-0154): the Expert's model/effort pick for the generation run.
  readonly model?: StudioModel;
  readonly effort?: ModelEffort;
}

export const startUnitPlan = (params: UnitPlanParams) =>
  post(
    "/api/curricula/create",
    { _tag: "generate", ...params },
    "The generation could not be started",
  ).pipe(
    Effect.flatMap(HttpClientResponse.schemaBodyJson(Schema.Struct({ documentId: DocumentId }))),
  );

export const cancelUnitPlan = (documentId: DocumentId) =>
  post("/api/generate/cancel", { documentId }, "The generation could not be cancelled").pipe(
    Effect.flatMap(
      HttpClientResponse.schemaBodyJson(
        Schema.Struct({ status: Schema.Literal("cancelled", "not-running") }),
      ),
    ),
  );

// ADR-0107 Decision 4, routed under `/api/curricula/job-runs` (CON-1316 retires
// `/api/unit-plan-launches/*`) rather than the retired path ADR-0107 originally landed on.
export const fetchUnitPlanInputs = (runId: string) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client.execute(
      HttpClientRequest.get(`/api/curricula/job-runs/inputs?runId=${encodeURIComponent(runId)}`),
    );
    return yield* HttpClientResponse.schemaBodyJson(UnitPlanInputsWire)(response);
  }).pipe(Effect.scoped);

// Fetched on demand, one document at a time, rather than ridden along on the run-list poll: these
// are whole documents and `/generate` polls every few seconds.
export const fetchUnitPlanInputContent = (runId: string, path: string) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client.execute(
      HttpClientRequest.get(
        `/api/curricula/job-runs/input-content?runId=${encodeURIComponent(runId)}&path=${encodeURIComponent(path)}`,
      ),
    );
    if (response.status >= 400) {
      const message = yield* failureMessage(response);
      return yield* Effect.fail(
        new UnitPlanRequestFailed({
          status: response.status,
          message: message ?? `The document could not be read (HTTP ${response.status}).`,
        }),
      );
    }
    return yield* HttpClientResponse.schemaBodyJson(Schema.Struct({ content: Schema.String }))(
      response,
    );
  }).pipe(Effect.scoped);

// `start` reports the server's own message rather than collapsing every failure into "couldn't
// start": the most likely refusal is the same-(cefr, l1, l2)-already-running guard, whose whole value
// is telling the Expert WHICH run is in the way and that it writes to the same place.
export type UnitPlanInputContentResult =
  | { readonly _tag: "ok"; readonly content: string }
  | { readonly _tag: "failed"; readonly message: string };

export type UnitPlanStartResult =
  | { readonly _tag: "started"; readonly documentId: DocumentId }
  | { readonly _tag: "failed"; readonly message: string };

export type UnitPlanCancelResult =
  | { readonly _tag: "cancelled" }
  | { readonly _tag: "not-running" }
  | { readonly _tag: "failed"; readonly message: string };

export const unitPlanActions = {
  start: (params: UnitPlanParams): Promise<UnitPlanStartResult> =>
    runtime.runPromise(
      startUnitPlan(params).pipe(
        Effect.map(({ documentId }): UnitPlanStartResult => ({ _tag: "started", documentId })),
        Effect.catchAll((error) =>
          Effect.succeed<UnitPlanStartResult>({
            _tag: "failed",
            message:
              error instanceof UnitPlanRequestFailed
                ? error.message
                : "The generation could not be started.",
          }),
        ),
      ),
    ),
  cancel: (documentId: DocumentId): Promise<UnitPlanCancelResult> =>
    runtime.runPromise(
      cancelUnitPlan(documentId).pipe(
        Effect.map(({ status }): UnitPlanCancelResult => ({ _tag: status })),
        Effect.catchAll((error) =>
          Effect.succeed<UnitPlanCancelResult>({
            _tag: "failed",
            message:
              error instanceof UnitPlanRequestFailed
                ? error.message
                : "The generation could not be cancelled.",
          }),
        ),
      ),
    ),
  inputs: (runId: string): Promise<UnitPlanInputsWire> =>
    runtime.runPromise(
      fetchUnitPlanInputs(runId).pipe(
        Effect.catchAll(() => Effect.succeed({ recorded: false, inputs: [] })),
      ),
    ),
  // Surfaces the failure rather than swallowing it: a document that will not load must say so, or
  // the panel silently implies the run had nothing to show.
  inputContent: (runId: string, path: string): Promise<UnitPlanInputContentResult> =>
    runtime.runPromise(
      fetchUnitPlanInputContent(runId, path).pipe(
        Effect.map(({ content }): UnitPlanInputContentResult => ({ _tag: "ok", content })),
        Effect.catchAll((error) =>
          Effect.succeed<UnitPlanInputContentResult>({
            _tag: "failed",
            message:
              error instanceof UnitPlanRequestFailed
                ? error.message
                : "The document could not be read.",
          }),
        ),
      ),
    ),
};
